import React, { useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, TextInput, panelGet } from '../../kit';
import {
  Banner, LIBRARY_KEY_PREFIX, Overview, Pill, SECTION, SPEC_TABLES, STATE_UNKNOWN, ServerWaitBanner, StatePill, Trigger,
  appliesTo, capText, categoryLabel, closedGates, fmtIST, fmtShortIST, num, specPrefix, waitingForServer,
} from './shared';
import { TriggerSwitch } from './Switches';
import { NextSendCell, Schedules } from './Schedule';
import { pctText } from '../dashboard/shared';

/**
 * MEMAC → Triggers: every trigger in the spec's tables (or by category), one
 * row each — id, trigger, applies to, CTA, max/month, template (a Saved
 * template, shared with Campaigns, is marked), would match N, last sent, the
 * On switch and the mode (the tags/AutoRules table shape).
 * A row opens the trigger's editor; "New trigger" opens an empty one.
 *
 * External rows (live mails sent by other code) show their real switch and
 * where it lives, and cannot be switched here.
 *
 * Each MEMAC row also says when it really goes out (its "When", from its
 * signal and the scheduler settings) and counts down to its next automatic
 * send: live (red) when every gate is open, "if enabled" (muted) otherwise —
 * hover for the gates that block it. "Preview" shows the mail each status ×
 * cohort segment would get now; "Preview all…" pages through every trigger.
 */

const SHOW = ['all', 'on', 'auto', 'off', 'external', 'custom'];
const SHOW_LABELS: Record<string, string> = {
  all: 'All triggers', on: 'Switched on', auto: 'On + Auto', off: 'Off', external: 'Live elsewhere', custom: 'Custom (made here)',
};
const GROUP_BY = ['spec', 'category'];
const GROUP_LABELS: Record<string, string> = { spec: 'Group: by table', category: 'Group: category' };

const Triggers: React.FC<{
  ov: Overview | null;
  triggers: Trigger[];
  razorpay: Record<string, any>[];
  loading: boolean; error: string;
  onReload: () => void;
  onOpen: (id: string) => void;
  /** memac_schedule: each trigger's When and next automatic send. */
  sch: Schedules;
  onElapsed?: () => void;
  onPreview: (id: string) => void;
  onPreviewAll: () => void;
  /** email/stats_triggers for the last 30 days, by trigger id (opened / clicked under Last sent). */
  stats30?: Record<string, any>;
  onOpenStats?: () => void;
}> = ({ ov, triggers, razorpay, loading, error, onReload, onOpen, sch, onElapsed, onPreview, onPreviewAll,
        stats30 = {}, onOpenStats }) => {
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');
  const [groupBy, setGroupBy] = useState('spec');
  const [counted, setCounted] = useState<Record<string, number | string>>({});
  const [flash, setFlash] = useState('');

  // A list that failed to read is not "no trigger is On + Auto": unknown.
  const autoOn = error ? null : triggers.filter(t => !t.external && t.enabled && t.mode === 'auto').length;
  const closed = closedGates(ov, autoOn);

  const shown = useMemo(() => triggers.filter(t => {
    if (show === 'on' && !(t.enabled && !t.external)) return false;
    if (show === 'auto' && !(t.enabled && t.mode === 'auto' && !t.external)) return false;
    if (show === 'off' && (t.enabled || t.external)) return false;
    if (show === 'external' && !t.external) return false;
    if (show === 'custom' && (t.builtin || t.external)) return false;
    const n = q.trim().toLowerCase();
    return !n || [t.id, t.name, t.template_key, t.template_name, t.cta_label, t.notes, t.description, ...t.templateKeys]
      .some(x => String(x || '').toLowerCase().includes(n));
  }), [triggers, show, q]);

  const groups = useMemo(() => {
    const out: { key: string; label: string; items: Trigger[] }[] = [];
    if (groupBy === 'spec') {
      SPEC_TABLES.forEach(g => {
        const items = shown.filter(t => specPrefix(t.id) === g.prefix);
        if (items.length) out.push({ key: g.prefix, label: g.label, items });
      });
    } else {
      const cats = ov?.categories || [];
      const keys = Array.from(new Set([...cats.map(c => c.key), ...shown.map(t => t.category)]));
      keys.forEach(k => {
        const items = shown.filter(t => t.category === k);
        if (items.length) out.push({ key: k, label: categoryLabel(k, cats), items });
      });
    }
    return out;
  }, [shown, groupBy, ov]);

  const count = async (t: Trigger) => {
    setCounted(c => ({ ...c, [t.id]: 'counting…' }));
    const { data, error: e } = await panelGet(SECTION, 'memac_dry_run', { trigger_id: t.id });
    setCounted(c => ({ ...c, [t.id]: e ? 'failed' : Number(data?.eligible ?? 0) }));
    if (e) setFlash(`${t.id}: ${e}`);
  };

  if (loading && !triggers.length) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      {error && <Msg kind="error">Triggers could not be read: {error}</Msg>}
      {flash && <Msg kind="error">{flash}</Msg>}
      {closed === null
        ? <Banner tone="warn"><b>{STATE_UNKNOWN}.</b> Whether anything is being sent automatically cannot be told
            until it reads — Refresh. A trigger marked “On · auto” may be sending.</Banner>
        : ov && waitingForServer(ov, autoOn)
        ? <ServerWaitBanner ov={ov} onOpenTrigger={onOpen}
            ids={triggers.filter(t => !t.external && t.enabled && t.mode === 'auto').map(t => t.id)} />
        : closed.length > 0
        ? <Banner tone="ok">Nothing is sent automatically right now — {closed.join('; ')}. Dry runs, tests
            and “Send to these N now” still work.</Banner>
        : <Banner tone="warn"><b>Automatic sending is on</b> for {autoOn} trigger{autoOn === 1 ? '' : 's'} (marked
            “On · auto” below). Switch a trigger off, or the global switch in Settings, to stop it.</Banner>}
      <Section title={`Triggers (${num(triggers.length)})`}
        right={<div className="flex gap-2 items-center flex-wrap">
          <Btn small onClick={onReload}>Refresh</Btn>
          <Btn small onClick={onPreviewAll}
            title="Page through the mail every chosen trigger would send to each status × cohort now. Sends nothing.">
            Preview all…
          </Btn>
          <Btn small kind="primary" onClick={() => onOpen('')}>New trigger</Btn>
        </div>}>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={q} onChange={setQ} placeholder="Find a trigger, template, CTA…" wide />
          <Select value={show} onChange={setShow} options={SHOW} labels={SHOW_LABELS} />
          <Select value={groupBy} onChange={setGroupBy} options={GROUP_BY} labels={GROUP_LABELS} />
        </div>
        <p className="text-[11px] text-grayscale-1100">
          Every trigger starts Off and Manual. Turning one on, or choosing Auto, asks first and says what will start
          happening. “Would match” is the last dry run's count (click it to count again — that reads only).
          “Next automatic send” counts down to the run at which it would next e-mail someone on its own — in red only
          when every gate is open, otherwise “if enabled”{sch.error ? ` (could not be worked out: ${sch.error})` : ''}.
        </p>
        {!shown.length ? (
          <div className="text-sm text-grayscale-1100 py-4 text-center">
            {triggers.length ? 'No trigger matches these filters.' : 'No triggers yet — the MEMAC backend has not seeded any.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Trigger</th>
                  <th className="py-2 pr-3">Applies to</th>
                  <th className="py-2 pr-3">CTA</th>
                  <th className="py-2 pr-3">Max / month</th>
                  <th className="py-2 pr-3">Template</th>
                  <th className="py-2 pr-3 text-right">Would match</th>
                  <th className="py-2 pr-3">Last sent</th>
                  <th className="py-2 pr-3" title="When it would next e-mail someone on its own (if enabled, while a gate is closed)">Next automatic send</th>
                  <th className="py-2 px-2 sticky right-0 bg-white" title="Kept in view when the table scrolls sideways">On · mode</th>
                </tr>
              </thead>
              {groups.map(g => (
                <tbody key={g.key}>
                  <tr className="bg-grayscale-200">
                    <td colSpan={10} className="py-1.5 px-2 text-xs font-bold text-grayscale-1300 font-grotesk">
                      {g.label} <span className="font-normal text-grayscale-1100">· {g.items.length}</span>
                    </td>
                  </tr>
                  {g.items.map(t => {
                    const c = counted[t.id];
                    const wm = c !== undefined ? c : t.would_match;
                    return (
                      <tr key={t.id} className={`border-b border-grayscale-200 align-top hover:bg-grayscale-100 ${t.enabled || t.external ? '' : 'opacity-80'}`}>
                        <td className={`py-2 pr-3 font-mono text-xs ${t.id.length > 8 ? 'max-w-[8rem]' : 'whitespace-nowrap'}`}>
                          <button type="button" onClick={() => onOpen(t.id)} className="hover:underline text-primary-700">{t.id}</button>
                        </td>
                        <td className="py-2 pr-3 min-w-[12rem]">
                          <button type="button" onClick={() => onOpen(t.id)} className="text-left font-medium text-grayscale-1300 hover:underline">
                            {t.name}
                          </button>
                          <div className="flex gap-1 flex-wrap mt-0.5">
                            <StatePill t={t} gates={closed} />
                            {t.is_service && <Pill tone="mute">service</Pill>}
                            {!t.builtin && !t.external && <Pill tone="blue">custom</Pill>}
                            {t.manual_only && !t.external && <Pill tone="mute">manual only</Pill>}
                          </div>
                          {t.external && <div className="text-[11px] text-grayscale-1100 mt-0.5">Switch: {t.ext?.where}</div>}
                          {!t.external && sch.byId[t.id]?.when && (
                            <div className="text-[11px] text-grayscale-1100 mt-0.5 max-w-[22rem] line-clamp-2"
                              title={sch.byId[t.id].when}>
                              <span className="font-semibold">When:</span> {sch.byId[t.id].when}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-grayscale-1200 min-w-[8rem] max-w-[11rem]">{t.external ? '—' : appliesTo(t)}</td>
                        <td className="py-2 pr-3 text-xs text-grayscale-1200">{t.cta_label || t.primary_cta || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-grayscale-1200 whitespace-nowrap">{t.external ? '—' : capText(t)}</td>
                        <td className="py-2 pr-3 text-xs text-grayscale-1200 max-w-[14rem]">
                          {t.external ? (t.templateKeys.join(', ') || '—')
                            : t.content_mode === 'template' ? (t.template_key
                              ? <>
                                <span title={t.template_key}>{t.template_name || t.template_key}</span>
                                {t.template_key.startsWith(LIBRARY_KEY_PREFIX) && (
                                  <div className="flex gap-1 flex-wrap mt-0.5">
                                    <Pill tone="blue" title="A Saved template, shared with Campaigns: edited under Email Center → Saved templates">
                                      Saved template
                                    </Pill>
                                    {t.template_archived && <Pill tone="warn">archived</Pill>}
                                  </div>
                                )}
                              </> : <span className="text-amber-800">none yet</span>)
                              : <span>{t.content_mode === 'design' ? 'Design' : 'Written here'}{t.content?.subject ? `: ${t.content.subject}` : ''}</span>}
                          {!t.external && !t.read_only && t.can_send && (
                            <div className="mt-0.5">
                              <button type="button" onClick={() => onPreview(t.id)}
                                title="The mail each status × cohort segment would get now, filled in for a member of it. Sends nothing."
                                className="text-[11px] text-primary-700 underline underline-offset-2">preview by cohort</button>
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums whitespace-nowrap">
                          {t.external || t.read_only ? '—' : (
                            <button type="button" onClick={() => count(t)} disabled={c === 'counting…'}
                              title={t.would_match_at ? `Counted ${fmtIST(t.would_match_at)} — click to count again` : 'Click to count now (reads only)'}
                              className="hover:underline font-medium">
                              {wm === null || wm === undefined ? 'count' : typeof wm === 'number' ? num(wm) : wm}
                            </button>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-grayscale-1200 whitespace-nowrap">
                          {t.last_sent_at ? <span title={fmtIST(t.last_sent_at)}>{fmtShortIST(t.last_sent_at)}</span> : '—'}
                          {t.sent_30d ? <div className="text-[10px] text-grayscale-1100">{num(t.sent_30d)} in 30 d</div> : null}
                          {stats30[t.id]?.sent ? (
                            <button type="button" onClick={onOpenStats}
                              title="Last 30 days, people's opens and clicks (team addresses left out) — open Stats for the full numbers and recipients"
                              className="block text-[10px] text-primary-700 hover:underline">
                              {stats30[t.id].open_rate === null || stats30[t.id].open_rate === undefined
                                ? 'opens n/a' : `${pctText(Number(stats30[t.id].open_rate))} opened`}
                              {' · '}{pctText(Number(stats30[t.id].click_rate) || 0)} clicked
                            </button>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3 min-w-[9rem]">
                          {t.external ? <span className="text-[11px] text-grayscale-1100">—</span>
                            : <NextSendCell s={sch.byId[t.id]} offsetMs={sch.offsetMs} loading={sch.loading} onElapsed={onElapsed} />}
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap sticky right-0 bg-white">
                          {t.external
                            ? <span className="text-[11px] text-grayscale-1100" title={t.ext?.where}>
                                {t.ext?.on === null || t.ext?.on === undefined ? 'live' : t.ext.on ? 'on (elsewhere)' : 'off (elsewhere)'}
                              </span>
                            : t.read_only
                              ? <button type="button" className="text-[11px] text-primary-700 underline underline-offset-2"
                                  onClick={() => onOpen(t.alias_of || t.id)}>{t.alias_of ? `switch ${t.alias_of}` : 'read-only'}</button>
                              : <TriggerSwitch t={t} ov={ov} onChanged={onReload} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Section>

      <Section title="Billing mails Razorpay sends itself (BL-1, external)">
        <p className="text-xs text-grayscale-1100 -mt-1">
          Subscriptions are created with Razorpay's customer notifications on, so Razorpay e-mails members directly.
          We only see its webhooks; MEMAC lists these so the matrix is complete and never sends them.
        </p>
        <DataTable rows={razorpay} empty="The server sent no list of Razorpay notices yet." maxHeight="18rem"
          columns={razorpay[0] && 'name' in razorpay[0]
            ? ['name', 'when', 'channel', 'webhook'].filter(k => k in razorpay[0]) : undefined} />
      </Section>
    </div>
  );
};

export default Triggers;
